/** Dispatch board (spec §14 SAFE-008…012): who's on duty, where, alerts, attempted addresses, name/address lookup. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDateTime } from '../shared/format.ts';
import { PinLegend, SlippyMap, type MapMarker } from '../shared/SlippyMap.tsx';
import { Badge, Empty, ErrorBox, Notice, useAction } from '../shared/ui.tsx';

interface Server {
  userId: string;
  name: string;
  badge: string | null;
  status: 'available' | 'on_scene' | 'unavailable';
  statusSince: string;
  dutySince: string | null;
  unavailableReason: string | null;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  locationAt: string | null;
  locationAgeMin: number | null;
  stationaryMin: number | null;
  lastActivity: string | null;
  lastActivityAt: string | null;
  idleMin: number;
  checkIn: 'ok' | 'checkin_due' | 'overdue' | 'paused';
  dueAt: string | null;
  alertAt: string | null;
  scene: { paperId: string; civilosNumber: string; defendant: string; address: string; lat: number | null; lng: number | null } | null;
}
interface SafetyEvent {
  id: string;
  kind: 'checkin_due' | 'overdue';
  reason: 'inactive' | 'on_scene';
  started_at: string;
  acknowledged_at: string | null;
  acknowledged_by_name: string | null;
  resolved_at: string | null;
  resolution: string | null;
  user_id: string;
  server_name: string;
  civilos_number: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
}
interface Attempt {
  id: string;
  at: string;
  outcome: string;
  outcomeLabel: string;
  civilosNumber: string;
  paperId: string;
  defendant: string;
  address: string;
  lat: number | null;
  lng: number | null;
  serverName: string;
}
interface Board {
  servers: Server[];
  events: SafetyEvent[];
  attempts: Attempt[];
  generatedAt: string;
}

const COLORS = { available: '#1f9d55', on_scene: '#e07b00', unavailable: '#8b95a5', checkin_due: '#e0a100', overdue: '#c9362b', stale: '#6b7a90' };
const SERVED = ['served'];
const initials = (n: string) => n.replace(/^(Deputy|Dep\.|Sgt\.|Officer)\s+/i, '').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

function serverColor(s: Server) {
  if (s.checkIn === 'overdue') return COLORS.overdue;
  if (s.locationAgeMin != null && s.locationAgeMin > 5) return COLORS.stale;
  return COLORS[s.status];
}

function statusText(s: Server) {
  if (s.status === 'on_scene') return 'Out of car';
  if (s.status === 'unavailable') return `Unavailable${s.unavailableReason ? ` — ${s.unavailableReason}` : ''}`;
  return 'Available';
}

function beep() {
  try {
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    const ctx = new Ctx();
    for (let i = 0; i < 3; i++) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 880;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.35);
      o.start(ctx.currentTime + i * 0.35);
      o.stop(ctx.currentTime + i * 0.35 + 0.2);
    }
  } catch {
    /* audio blocked until the page is clicked */
  }
}

export function Dispatch() {
  const { can } = useAuth();
  const [data, setData] = useState<Board | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [days, setDays] = useState(1);
  const [tab, setTab] = useState<'duty' | 'attempts' | 'search' | 'messages'>('duty');
  const [msgTo, setMsgTo] = useState<string | null>(null);
  const [inbox, setInbox] = useState<{ messages: any[]; unreadByUser: Record<string, number> }>({ messages: [], unreadByUser: {} });
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<any[] | null>(null);
  const [showAttempts, setShowAttempts] = useState(true);
  const seen = useRef(new Set<string>());
  const first = useRef(true);
  const a = useAction();
  const s = useAction();

  const load = async () => {
    try {
      const b = await api.get<Board>(`/api/dispatch/board?days=${days}`);
      setData(b);
      setError(null);
      api.get('/api/dispatch/messages').then(setInbox).catch(() => undefined);
      const newOverdue = b.events.filter((e) => e.kind === 'overdue' && !e.resolved_at && !seen.current.has(e.id));
      newOverdue.forEach((e) => seen.current.add(e.id));
      if (newOverdue.length && !first.current) beep();
      first.current = false;
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const open = (data?.events ?? []).filter((e) => !e.resolved_at);
  const alerts = open.filter((e) => e.kind === 'overdue' || !open.some((o) => o.kind === 'overdue' && o.user_id === e.user_id));

  const markers: MapMarker[] = useMemo(() => {
    const m: MapMarker[] = [];
    if (showAttempts)
      for (const x of data?.attempts ?? [])
        if (x.lat != null && x.lng != null)
          m.push({ id: `a:${x.id}`, lat: x.lat, lng: x.lng, dot: true, color: SERVED.includes(x.outcome) ? '#1f9d55' : '#5b7db8', title: `${x.address} — ${x.outcomeLabel} by ${x.serverName}, ${fmtDateTime(x.at)}` });
    for (const r of results ?? []) if (r.lat != null) m.push({ id: `r:${r.paperId}`, lat: r.lat, lng: r.lng, color: '#7b3fb3', title: `${r.defendant} — ${r.address}`, selected: sel === `r:${r.paperId}` });
    for (const x of data?.servers ?? []) {
      if (x.lat == null || x.lng == null) continue;
      m.push({ id: `s:${x.userId}`, lat: x.lat, lng: x.lng, color: serverColor(x), label: initials(x.name), title: `${x.name} — ${statusText(x)}${x.scene ? ` at ${x.scene.address}` : ''}`, selected: sel === `s:${x.userId}` });
    }
    return m;
  }, [data, results, showAttempts, sel]);

  const doSearch = () =>
    s.run(async () => {
      const r = await api.get<{ results: any[] }>(`/api/dispatch/search?q=${encodeURIComponent(q.trim())}`);
      setResults(r.results);
      setTab('search');
    });

  const unreadTotal = Object.values(inbox.unreadByUser).reduce((a, b) => a + b, 0);
  const selected = sel?.startsWith('s:') ? data?.servers.find((x) => `s:${x.userId}` === sel) : null;

  return (
    <div className="stack dispatch">
      <div className="row between wrap gap">
        <h1>Dispatch</h1>
        <form
          className="row gap-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (q.trim().length >= 2) doSearch();
          }}
        >
          <input aria-label="Search name or address" placeholder="Search name, address or paper #" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 280 }} />
          <button className="btn primary" disabled={s.busy || q.trim().length < 2}>
            Search
          </button>
          {results && (
            <button type="button" className="btn ghost" onClick={() => (setResults(null), setQ(''), setTab('duty'))}>
              Clear
            </button>
          )}
        </form>
      </div>
      <ErrorBox error={error ?? s.error} />

      {alerts.map((e) => (
        <div key={e.id} className={`dispatch-alert ${e.kind}`} role="alert">
          <div className="grow">
            <strong>NO RESPONSE — {e.server_name}</strong>
            <div className="small">
              {e.reason === 'on_scene' ? `Out of car${e.address ? ` at ${e.address}` : ''}${e.civilos_number ? ` (${e.civilos_number})` : ''} with no activity` : 'No app activity'} — didn’t answer “Are you OK?” — alert raised {fmtDateTime(e.started_at)}.
              {e.acknowledged_at && ` Acknowledged by ${e.acknowledged_by_name}.`}
            </div>
          </div>
          {can('safety.respond') && (
            <div className="row gap-sm">
              {e.lat != null && (
                <button className="btn sm" onClick={() => setSel(`s:${e.user_id}`)}>
                  Show
                </button>
              )}
              {!e.acknowledged_at && e.kind === 'overdue' && (
                <button className="btn sm primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/dispatch/events/${e.id}/ack`), load()))}>
                  Acknowledge
                </button>
              )}
              <button
                className="btn sm"
                disabled={a.busy}
                onClick={() => {
                  const note = window.prompt(`How was ${e.server_name} confirmed OK? (e.g. "Reached by radio")`)?.trim();
                  if (note) a.run(async () => (await api.post(`/api/dispatch/events/${e.id}/resolve`, { note }), load()));
                }}
              >
                Confirmed OK
              </button>
            </div>
          )}
        </div>
      ))}
      <ErrorBox error={a.error} />

      <div className="map-layout">
        <div className="stack sm">
          <SlippyMap height={620} markers={markers} fitKey={`${(data?.servers ?? []).map((x) => x.userId).join()}|${results?.length ?? 0}|${days}|${showAttempts}`} ariaLabel="Dispatch map" onMarkerClick={setSel} />
          <div className="row between wrap gap">
            <PinLegend
              items={[
                { color: COLORS.available, label: 'Available' },
                { color: COLORS.on_scene, label: 'Out of car' },
                { color: COLORS.overdue, label: 'No response' },
                { color: COLORS.stale, label: 'Location >5 min old' },
                { color: '#5b7db8', label: 'Attempted' },
                { color: '#1f9d55', label: 'Served' },
              ]}
            />
            <div className="row gap-sm small">
              <label className="row gap-sm">
                <input type="checkbox" checked={showAttempts} onChange={(e) => setShowAttempts(e.target.checked)} /> Attempted addresses
              </label>
              <select aria-label="Attempt period" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </div>
          </div>
        </div>

        <div className="map-side">
          {selected && (
            <div className="map-popup">
              <div className="row between">
                <strong>{selected.name}</strong>
                {selected.badge && <span className="muted small">#{selected.badge}</span>}
              </div>
              <ServerDetails s={selected} />
              <div className="row gap-sm">
                <button className="btn sm primary" onClick={() => (setMsgTo(selected.userId), setTab('messages'))}>
                  Message
                </button>
                <button className="btn sm ghost" onClick={() => setSel(null)}>
                  Close
                </button>
              </div>
            </div>
          )}
          <div className="tabs" role="tablist">
            {(
              [
                ['duty', `On duty (${data?.servers.length ?? 0})`],
                ['attempts', `Attempts (${data?.attempts.length ?? 0})`],
                ['search', `Search${results ? ` (${results.length})` : ''}`],
                ['messages', `Messages${unreadTotal ? ` (${unreadTotal} new)` : ''}`],
              ] as const
            ).map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                {l}
              </button>
            ))}
          </div>

          {tab === 'duty' && (
            <ul className="stop-list">
              {data && data.servers.length === 0 && <Empty>No one is on duty.</Empty>}
              {data?.servers.map((x) => (
                <li key={x.userId}>
                  <button className={sel === `s:${x.userId}` ? 'active' : ''} onClick={() => setSel(`s:${x.userId}`)}>
                    <span className="stop-num" style={{ background: serverColor(x) }}>
                      {initials(x.name)}
                    </span>
                    <span className="grow">
                      <span>
                        <strong>{x.name}</strong> · {statusText(x)}
                      </span>
                      {x.scene && <span className="small"> · {x.scene.address}</span>}
                      <span className="muted small">
                        {' '}
                        · {x.status === 'on_scene' ? `${Math.max(0, Math.round((Date.parse(data.generatedAt) - Date.parse(x.statusSince)) / 60000))} min out of car · ` : ''}
                        idle {x.idleMin} min{x.stationaryMin != null ? ` · still ${x.stationaryMin} min` : ''}
                        {x.locationAgeMin == null ? ' · no location yet' : x.locationAgeMin > 5 ? ` · location ${x.locationAgeMin} min old` : ''}
                      </span>
                      {x.checkIn === 'overdue' && <Badge tone="bad">No response</Badge>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {tab === 'attempts' && (
            <ul className="stop-list">
              {data && data.attempts.length === 0 && <Empty>No attempts in this period.</Empty>}
              {data?.attempts.map((x) => (
                <li key={x.id}>
                  <button className={sel === `a:${x.id}` ? 'active' : ''} onClick={() => setSel(`a:${x.id}`)}>
                    <span className="stop-num" style={{ background: SERVED.includes(x.outcome) ? '#1f9d55' : '#5b7db8' }}>
                      •
                    </span>
                    <span className="grow">
                      <span>{x.address}</span>
                      <span className="muted small">
                        {' '}
                        · {x.outcomeLabel} · {x.serverName} · {fmtDateTime(x.at)} · {x.defendant} ({x.civilosNumber})
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {tab === 'search' && (
            <div className="stack sm">
              {!results && <p className="muted small">Search a person’s name, an address, or a paper number to see every attempt made there.</p>}
              {results?.length === 0 && <Empty>No matches.</Empty>}
              {results?.map((r) => (
                <div key={r.paperId} className={`map-popup ${sel === `r:${r.paperId}` ? 'selected' : ''}`} onClick={() => setSel(`r:${r.paperId}`)}>
                  <div className="row between">
                    <strong>{r.defendant}</strong>
                    <span className="mono small">{r.civilosNumber}</span>
                  </div>
                  <div className="small">{r.address}</div>
                  <div className="muted small">
                    {r.status.replace(/_/g, ' ').toLowerCase()} · {r.serverName ?? 'unassigned'}
                  </div>
                  {r.attempts.length === 0 ? (
                    <div className="small muted">No attempts yet.</div>
                  ) : (
                    <ul className="plain small">
                      {r.attempts.map((x: any, i: number) => (
                        <li key={i}>
                          {fmtDateTime(x.at)} — {x.outcomeLabel} — {x.serverName}
                          {x.outsideRadius ? <Badge tone="warn">outside radius</Badge> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {tab === 'messages' && <DeskMessages servers={data?.servers ?? []} inbox={inbox} to={msgTo} setTo={setMsgTo} reload={load} />}
          {data && <p className="small muted">Updates every 10 seconds · last {fmtDateTime(data.generatedAt)}</p>}
          <Notice tone="info">Deputies share location only while on duty. Check-in alerts come from the server’s clock, so they still fire if a phone dies or loses signal.</Notice>
        </div>
      </div>
    </div>
  );
}

function ServerDetails({ s }: { s: Server }) {
  return (
    <div className="small stack sm">
      <div>
        {statusText(s)} {s.checkIn === 'overdue' ? <Badge tone="bad">No response</Badge> : null}
      </div>
      {s.scene && (
        <div>
          At <strong>{s.scene.address}</strong> — {s.scene.defendant} ({s.scene.civilosNumber}) since {fmtDateTime(s.statusSince)}
        </div>
      )}
      <div className="muted">
        On duty since {fmtDateTime(s.dutySince)} · last activity: {s.lastActivity ?? '—'} ({fmtDateTime(s.lastActivityAt)})
      </div>
      <div className="muted">
        Location {s.locationAt ? `${fmtDateTime(s.locationAt)}${s.accuracyM != null ? ` (±${s.accuracyM} m)` : ''}` : 'not received yet'}
        {s.stationaryMin != null && ` · hasn’t moved for ${s.stationaryMin} min`}
      </div>
      {s.checkIn !== 'paused' && s.dueAt && <div className="muted">Phone asks “Are you OK?” at {fmtDateTime(s.dueAt)} · alert if unanswered {fmtDateTime(s.alertAt)}</div>}
    </div>
  );
}

function DeskMessages({ servers, inbox, to, setTo, reload }: { servers: Server[]; inbox: { messages: any[]; unreadByUser: Record<string, number> }; to: string | null; setTo: (id: string | null) => void; reload: () => void }) {
  const [thread, setThread] = useState<any[]>([]);
  const [body, setBody] = useState('');
  const a = useAction();
  // Deputies on duty plus anyone who has written in.
  const people = new Map<string, string>();
  servers.forEach((x) => people.set(x.userId, x.name));
  inbox.messages.forEach((m) => m.to_user_id === null && people.set(m.from_user_id, m.from_name));
  const unread = to ? inbox.unreadByUser[to] ?? 0 : 0;
  useEffect(() => {
    if (!to) return setThread([]);
    api.get(`/api/dispatch/messages?userId=${to}`).then((r) => setThread(r.messages)).catch(() => undefined);
    if (unread) api.post('/api/dispatch/messages/read', { userId: to }).then(reload).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, inbox.messages.length]);
  return (
    <div className="stack sm">
      <select aria-label="Deputy" value={to ?? ''} onChange={(e) => setTo(e.target.value || null)}>
        <option value="">Choose a deputy…</option>
        {[...people].map(([id, name]) => (
          <option key={id} value={id}>
            {name}
            {inbox.unreadByUser[id] ? ` (${inbox.unreadByUser[id]} new)` : ''}
          </option>
        ))}
      </select>
      {to && (
        <>
          <div className="desk-thread">
            {thread.length === 0 && <p className="muted small">No messages yet.</p>}
            {thread.map((m) => (
              <div key={m.id} className={`msg ${m.to_user_id ? 'mine' : 'theirs'}`}>
                <div className="msg-b">{m.body}</div>
                <div className="msg-m">
                  {m.from_name} · {fmtDateTime(m.created_at)}
                  {m.to_user_id && (m.read_at ? ' · read' : ' · not read yet')}
                </div>
              </div>
            ))}
          </div>
          <form
            className="row gap-sm"
            onSubmit={(e) => {
              e.preventDefault();
              a.run(async () => {
                const r = await api.post('/api/dispatch/messages', { toUserId: to, body });
                setThread(r.messages);
                setBody('');
                reload();
              });
            }}
          >
            <input className="grow" aria-label="Message" value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Message ${people.get(to) ?? 'deputy'}…`} maxLength={2000} />
            <button className="btn primary" disabled={!body.trim() || a.busy}>
              Send
            </button>
          </form>
          <ErrorBox error={a.error} />
        </>
      )}
    </div>
  );
}
